import React, { useState, useEffect } from "react";
import { Responsive, WidthProvider } from "react-grid-layout";
import "react-resizable/css/styles.css";
import "react-grid-layout/css/styles.css";
import { BASE_URL } from "../Components/Constant";
import ApiService from "../Components/apiService";
import Visuals from "../Components/Visuals";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Switch, Tooltip, Spin } from "antd";
import Cookies from "js-cookie";
import CRMLandingPage from "../LandingPages/CRMLandingPage";
import eventBus from "../Components/eventBus"; // Import event bus

const ResponsiveGridLayout = WidthProvider(Responsive);
const breakpoints = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 320 };
const cols = { lg: 18, md: 18, sm: 9, xs: 6, xxs: 3 };
const rowHeight = 5;

const HomePage = () => {
  const [components, setComponents] = useState([]);
  const [layout, setLayout] = useState([]);
  const [defaultDashboard, setDefaultDashboard] = useState([]);
  const [viewModes, setViewModes] = useState({});
  const [token, setToken] = useState(Cookies.get("tokenRes") || "");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (token) {
      getDashboards();
    }
  }, [token]);

  // Listen for app changes and refetch dashboard
  useEffect(() => {
    const handleAppChange = (eventData) => {
      console.log('App changed, refetching dashboard:', eventData);
      if (token) {
        getDashboards();
      }
    };

    // Subscribe to AppChanged event
    eventBus.on('AppChanged', handleAppChange);

    // Cleanup function to remove event listener
    return () => {
      eventBus.off('AppChanged', handleAppChange);
    };
  }, [token]); // Include token as dependency to ensure we only fetch when token exists

  const getDashboards = async () => {
    try {
      setLoading(true);
      
      // Get app ID from cookies
      const selectedAppId = Cookies.get('selectedAppId');
      
      // Construct URL with app_id parameter if available
      const url = selectedAppId 
        ? `${BASE_URL}/get_dashboards?app_id=${selectedAppId}`
        : `${BASE_URL}/get_dashboards`;
      
      const apiService = new ApiService(url, {
        "Content-Type": "application/json",
      }, "GET");
      const res = await apiService.makeCall();
      console.log("Dashboard data:", res);

      const defaultDash = res;
      setDefaultDashboard(defaultDash);

      if (defaultDash) {
        const mappedLayout = defaultDash?.dashboard_layout?.map((item) => ({
          ...item?.position,
          i: item?.position?.i?.toString(),
        }));

        const mappedComponents = defaultDash?.components?.map((item) => ({
          ...item?.info,
        }));

        setComponents(mappedComponents);
        setLayout(mappedLayout);
      } else {
        console.error("No default dashboard found");
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewChange = (checked, componentId) => {
    setViewModes((prev) => ({
      ...prev,
      [componentId]: checked,
    }));
  };

  const getHighlightRowStyle = (record, component) => {
    if (
      !component?.multipleHighlights ||
      !record ||
      !Array.isArray(component?.multipleHighlights)
    ) {
      return {};
    }

    for (const highlight of component?.multipleHighlights) {
      if (!highlight?.filters || Object.keys(highlight?.filters).length === 0) {
        continue;
      }

      const filters = Object.values(highlight?.filters);
      const logic = highlight?.filterLogic || "AND";

      const matchResults = filters.map((filter) => {
        if (!filter.field || filter.field === "") return false;

        const recordValue = record[filter.field];
        const filterValue =
          typeof filter.value === "object"
            ? Object.values(filter.value)[0]
            : filter.value;

        if (typeof filter.value === "object" && filter.value !== null) {
          const operator = Object.keys(filter.value)[0];
          switch (operator) {
            case "equal":
              return recordValue === filterValue;
            case "not_equal":
              return recordValue !== filterValue;
            case "contains":
              return (
                recordValue &&
                recordValue
                  .toString()
                  .toLowerCase()
                  .includes(filterValue.toString().toLowerCase())
              );
            case "not_contains":
              return (
                !recordValue ||
                !recordValue
                  .toString()
                  .toLowerCase()
                  .includes(filterValue.toString().toLowerCase())
              );
            case "starts_with":
              return (
                recordValue &&
                recordValue
                  .toString()
                  .toLowerCase()
                  .startsWith(filterValue.toString().toLowerCase())
              );
            case "ends_with":
              return (
                recordValue &&
                recordValue
                  .toString()
                  .toLowerCase()
                  .endsWith(filterValue.toString().toLowerCase())
              );
            case "greater_than":
              return Number(recordValue) > Number(filterValue);
            case "less_than":
              return Number(recordValue) < Number(filterValue);
            case "greater_equal":
              return Number(recordValue) >= Number(filterValue);
            case "less_equal":
              return Number(recordValue) <= Number(filterValue);
            default:
              return recordValue === filterValue;
          }
        } else {
          return recordValue === filterValue;
        }
      });

      let matches = false;
      if (logic === "AND") {
        matches = matchResults.length > 0 && matchResults.every((r) => r);
      } else if (logic === "OR") {
        matches = matchResults.some((r) => r);
      } else if (logic === "1" && matchResults.length === 1) {
        matches = matchResults[0];
      }

      if (matches) {
        return {
          style: {
            backgroundColor: highlight?.backgroundColor,
            border: `1px solid ${highlight?.borderColor}`,
            color: highlight?.textColor,
            fontWeight: highlight?.bold ? "bold" : "normal",
          },
          message: highlight?.message || null,
        };
      }
    }
    return { style: {}, message: null };
  };

  // ✅ If token is not present, show CRM Landing Page
  if (!token) {
    return <CRMLandingPage />;
  }

  return (
    <Spin spinning={loading} size="large" tip="Loading dashboard...">
      <div style={{ margin: "0", padding: "0", minHeight: "100vh" }}>
        <style>{`
          .react-resizable-handle {
            display: none !important;
          }
        `}</style>

        {defaultDashboard ? (
        <div style={{ ...styles.cardsContainer, gap: "4px" }}>
          <ResponsiveGridLayout
            className="my-1 mx-1"
            breakpoints={breakpoints}
            cols={cols}
            layout={layout}
            rowHeight={rowHeight}
            draggableCancel=".non-draggable"
            isDraggable={(layout, oldItem) =>
              !components.find(
                (component) => component?.id.toString() === oldItem.i
              ).isStatic
            }
            isResizable={false}
          >
            {components.map((component) => (
              <div
                className={`grid-cell non-draggable`}
                key={component?.id}
                data-grid={layout.find(
                  (item) => item.i === component?.id.toString()
                )}
                style={{
                  ...(component?.type === "metric"
                    ? styles.metricItem
                    : { ...styles.gridItem, padding: "8px" }),
                  overflow: "hidden",
                  minHeight: 120,
                  minWidth: 180,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div style={{ ...styles.gridHeader, padding: "2px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <h2 style={{ margin: 0 }}>
                      {component?.name || component?.content}
                    </h2>
                    {component?.reportId && (
                      <Tooltip title={`View Full Report`}>
                        <a
                          href={`/ViewReport/${component?.reportId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "#209aff",
                            marginLeft: 2,
                            fontSize: 11,
                          }}
                          title="View Full Report"
                        >
                          <InfoCircleOutlined />
                        </a>
                      </Tooltip>
                    )}
                  </div>
                  {component?.type !== "metric" &&
                    component?.type !== "table" &&
                    component?.type !== "myTasks" &&
                    component?.type !== "logBook" &&
                    component?.type !== "approval" && (
                      <Tooltip
                        title={`${
                          !!viewModes[component?.id]
                            ? "Get Brief"
                            : "Get Insight"
                        }`}
                      >
                        <Switch
                          size="small"
                          checkedChildren="Details"
                          unCheckedChildren="Chart"
                          checked={!!viewModes[component?.id]}
                          onChange={(checked) =>
                            handleViewChange(checked, component?.id)
                          }
                          style={{ marginLeft: 2 }}
                        />
                      </Tooltip>
                    )}
                </div>
                {component?.type ? (
                  <Visuals
                    type={
                      !!viewModes[component?.id]
                        ? "table"
                        : component?.type
                    }
                    reportId={component?.reportId}
                    component={component}
                    isEditingWindow={false}
                    getHighlightRowStyle={getHighlightRowStyle}
                  />
                ) : (
                  <div style={styles.placeholder}>
                    Please configure your widget.
                  </div>
                )}
              </div>
            ))}
          </ResponsiveGridLayout>
        </div>
      ) : (
        <div style={styles.noDashboard}>
          <h2>No Dashboards Available</h2>
        </div>
      )}
      </div>
    </Spin>
  );
};

const styles = {
  cardsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    width: "100%",
    justifyContent: "center",
    flexGrow: 1,
    height: "auto",
  },
  gridItem: {
    position: "relative",
    border: "1px solid rgb(230, 228, 228)",
    borderRadius: "22px",
    padding: "2px",
    backgroundColor: "#fff",
    height: "auto",
  },
  metricItem: {
    position: "relative",
    backgroundColor: "transparent",
    height: "auto",
    border: "none",
    boxShadow: "none",
    padding: 0,
  },
  gridHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "2px",
    paddingBottom: "6px",
    fontSize: "0.6rem",
  },
  placeholder: {
    color: "#aaa",
    textAlign: "center",
    marginTop: "10px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
  },
  noDashboard: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    color: "#aaa",
  },
};

export default HomePage;
